import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useCartContext } from '@context/cartContext';
import confirmOrderPayment from '@utils/confirmOrderPayment';
import { Helmet } from 'react-helmet';

// Rückkehr aus einer Redirect-Zahlung (Twint, 3DS …). Die return_url der
// Checkout Session trägt orderId und session_id; der Server prüft die Session
// und schliesst die Bestellung ab.
//
// Bestellungen aus der Zeit vor den Checkout Sessions kehren noch mit
// redirect_status zurück (PaymentIntent). Die schliesst der Stripe-Webhook
// ab — hier wird nur der Stand angezeigt.
const LEGACY_STATUS = {
  succeeded: 'paid',
  processing: 'processing',
  failed: 'open',
};

const PaymentComplete = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const sessionId = searchParams.get('session_id');
  const redirectStatus = searchParams.get('redirect_status');

  const [status, setStatus] = useState(
    sessionId ? 'loading' : (LEGACY_STATUS[redirectStatus] ?? 'error')
  );

  const navigate = useNavigate();
  const { clearCart } = useCartContext();

  useEffect(() => {
    if (!sessionId || !orderId) return;
    let active = true;
    confirmOrderPayment(orderId).then((result) => {
      if (active) setStatus(result ?? 'error');
    });
    return () => {
      active = false;
    };
  }, [sessionId, orderId]);

  useEffect(() => {
    // Bezahlt oder unterwegs: der Warenkorb ist bestellt.
    if (status === 'paid' || status === 'processing') clearCart();
    if (status === 'paid') {
      const timer = setTimeout(() => navigate('/'), 5000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  if (status === 'loading') {
    return (
      <main className="py-24">
        <div className="px-4 py-16 text-center sm:px-6 lg:px-8">
          <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
            Zahlung wird geprüft…
          </h1>
        </div>
      </main>
    );
  }

  if (status === 'processing') {
    return (
      <main className="py-24">
        <div className="px-4 py-16 text-center sm:px-6 lg:px-8">
          <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
            Deine Zahlung wird verarbeitet…
          </h1>
        </div>
        <div className="text-center px-6">
          <p>Das kann je nach Zahlungsmethode einen Moment dauern.</p>
          <p>
            Du erhältst eine Bestellbestätigung per E-Mail, sobald die Zahlung
            abgeschlossen ist.
          </p>
        </div>
      </main>
    );
  }

  if (status === 'paid') {
    return (
      <main className="py-24">
        <div className="px-4 py-16 text-center sm:px-6 lg:px-8">
          <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
            Danke für deine Bestellung!
          </h1>
        </div>
        <div className="text-center px-6">
          <p>Deine Zahlung war erfolgreich!</p>
          <p>Du wirst gleich zur Startseite weitergeleitet.</p>
        </div>
        <Helmet>
          <script type="text/javascript">
            {`
            gtag('event', 'conversion_event_purchase', {
              // Add your event parameters here if needed
            });
          `}
          </script>
        </Helmet>
      </main>
    );
  }

  // 'open' / 'expired': nicht bezahlt. 'error': Stand unbekannt.
  return (
    <main className="py-24">
      <div className="px-4 py-16 text-center sm:px-6 lg:px-8">
        <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
          Da ist etwas schiefgelaufen...
        </h1>
      </div>
      <div className="text-center px-6">
        {status === 'error' ? (
          <p>
            Wir konnten den Stand deiner Zahlung gerade nicht prüfen. Falls sie
            durchgegangen ist, erhältst du eine Bestätigung per E-Mail.
          </p>
        ) : (
          <>
            <p>Deine Zahlung wurde nicht abgeschlossen.</p>
            <p>Bitte versuche es nochmal.</p>
          </>
        )}
        <Link
          to="/warenkorb"
          className="mt-6 inline-block font-medium text-svisit-primary-600 hover:text-svisit-primary-700"
        >
          Zurück zum Warenkorb
        </Link>
      </div>
    </main>
  );
};

export default PaymentComplete;
